import packageInfo from "../package.json";
import { SearchVariables } from "./hooks/makeSearch";
import { ListSettings, ListViews, Pagination } from "./types";

export const APP_MOUNT_URI = process.env.APP_MOUNT_URI;
export const API_URI = process.env.API_URI;
export const BOSS_URI = process.env.BOSS_URI;

export const DEFAULT_INITIAL_SEARCH_DATA: SearchVariables = {
  after: null,
  first: 20,
  query: ""
};

export const DEFAULT_INITIAL_PAGINATION_DATA: Pagination = {
  after: undefined,
  before: undefined
};

export const PAGINATE_BY = 20;

export type ProductListColumns = "status" | "visibility" | "price";
export type OrderDetailsListColumns = "product" | "qty" | "status";
export interface AppListViewSettings {
  [ListViews.ANNOUNCEMENT_LIST]: ListSettings;
  [ListViews.APPS_LIST]: ListSettings;
  [ListViews.BANNER_LIST]: ListSettings;
  [ListViews.BRANDS_LIST]: ListSettings;
  [ListViews.BULK_VOUCHER_LIST]: ListSettings;
  [ListViews.BRANDGROUPS_LIST]: ListSettings;
  [ListViews.CATEGORY_LIST]: ListSettings;
  [ListViews.CLUSTER_LIST]: ListSettings;
  [ListViews.COLLECTION_LIST]: ListSettings;
  [ListViews.COUPON_LIST]: ListSettings;
  [ListViews.COUPON_REDEMPTION_LIST]: ListSettings;
  [ListViews.CUSTOMER_LIST]: ListSettings;
  [ListViews.DRAFT_LIST]: ListSettings;
  [ListViews.DISPATCH_LIST]: ListSettings;
  [ListViews.INVENTORY_LIST]: ListSettings;
  [ListViews.MALL_LIST]: ListSettings;
  [ListViews.NAVIGATION_LIST]: ListSettings;
  [ListViews.ORDER_LIST]: ListSettings;
  [ListViews.PAGES_LIST]: ListSettings;
  [ListViews.PLUGINS_LIST]: ListSettings;
  [ListViews.PRODUCT_LIST]: ListSettings<ProductListColumns>;
  [ListViews.REMITTANCE_LIST]: ListSettings;
  [ListViews.REPORTS_LIST]: ListSettings;
  [ListViews.SALES_LIST]: ListSettings;
  [ListViews.SHIPPING_METHODS_LIST]: ListSettings;
  [ListViews.STAFF_MEMBERS_LIST]: ListSettings;
  [ListViews.PERMISSION_GROUP_LIST]: ListSettings;
  [ListViews.VOUCHER_LIST]: ListSettings;
  [ListViews.WAREHOUSE_LIST]: ListSettings;
  [ListViews.WEBHOOK_LIST]: ListSettings;
  [ListViews.PRODUCT_STOCKS]: ListSettings;
  [ListViews.STORE_LIST]: ListSettings;
  [ListViews.STORE_REPORT_STOCKS]: ListSettings;
  [ListViews.PAYOUT_DETAILS_LIST]: ListSettings;
  [ListViews.APPCONFIG_LIST]: ListSettings;
  [ListViews.APPCONFIGDETAIL_LIST]: ListSettings;
  [ListViews.PAYMENT_EVENTS]: ListSettings;
  [ListViews.ORDER_DETAILS_LIST]: ListSettings<OrderDetailsListColumns>;
  [ListViews.COUPON_COLLECTION_LIST]: ListSettings;
  [ListViews.COUPON_COLLECTION_ASSIGNED_COUPONS_LIST]: ListSettings;
}

export const defaultListSettings: AppListViewSettings = {
  [ListViews.ANNOUNCEMENT_LIST]: {
    rowNumber: 9
  },
  [ListViews.APPS_LIST]: {
    rowNumber: 10
  },
  [ListViews.BANNER_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.BULK_VOUCHER_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.APPCONFIG_LIST]: {
    rowNumber: 100
  },
  [ListViews.APPCONFIGDETAIL_LIST]: {
    rowNumber: 100
  },
  [ListViews.BRANDS_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.BRANDGROUPS_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.CATEGORY_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.CLUSTER_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.COLLECTION_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.COUPON_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.COUPON_REDEMPTION_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.CUSTOMER_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.DRAFT_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.DISPATCH_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.INVENTORY_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.MALL_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.NAVIGATION_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.ORDER_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.PAGES_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.PLUGINS_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.PRODUCT_LIST]: {
    columns: ["status", "price"],
    rowNumber: PAGINATE_BY
  },
  [ListViews.SALES_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.SHIPPING_METHODS_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.STAFF_MEMBERS_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.PERMISSION_GROUP_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.REMITTANCE_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.VOUCHER_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.WAREHOUSE_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.WEBHOOK_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.STORE_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.PAYMENT_EVENTS]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.STORE_REPORT_STOCKS]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.PRODUCT_STOCKS]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.REPORTS_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.PAYOUT_DETAILS_LIST]: {
    rowNumber: PAGINATE_BY
  },
  [ListViews.ORDER_DETAILS_LIST]: {
    columns: ["product", "qty", "status"],
    rowNumber: PAGINATE_BY
  },
  [ListViews.COUPON_COLLECTION_LIST]: {
    rowNumber: 10
  },
  [ListViews.COUPON_COLLECTION_ASSIGNED_COUPONS_LIST]: {
    rowNumber: 10
  }
};

export const APP_VERSION = packageInfo.version;
export const DEMO_MODE = process.env.DEMO_MODE === "true";
export const GTM_ID = process.env.GTM_ID;

export const DEFAULT_NOTIFICATION_SHOW_TIME = 3000;

export const MAX_DOWNLOAD_LIMIT = 5;

export const IS_TOKEN_FROM_COOKIES =
  String(process.env.TOKEN_FROM_COOKIES) === "true";
